import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { apiRequest } from "../../api/http";
import { PayrollDetail } from "../../types/admin";
import { buildPayrollFileName, getPayrollFileUrl, getPayrollTitle, getPayrollTypeLabel, isPayrollFileOnly, isProbablyMockPayrollFileUrl } from "../../utils/payroll";

const BranchPayrollDetailPage = () => {
  const { branchId, payrollId } = useParams();
  const navigate = useNavigate();
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const [detail, setDetail] = useState<PayrollDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const detailFileUrl = getPayrollFileUrl(detail);
  const detailIsFileOnly = isPayrollFileOnly(detail);
  const canPreviewDetailFile = Boolean(detailFileUrl) && !isProbablyMockPayrollFileUrl(detailFileUrl);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await apiRequest<PayrollDetail>(`/admin/branches/${branchId}/payrolls/${payrollId}`);
        setDetail(res);
      } catch (err: any) {
        setError(err.message || "급여명세서 상세정보를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };
    if (branchId && payrollId) {
      fetchDetail();
    }
  }, [branchId, payrollId]);

  const handleDownloadPdf = async () => {
    if (!detail) return;

    if (detailIsFileOnly) {
      if (!canPreviewDetailFile) {
        alert("다운로드할 급여명세서 파일이 없습니다.");
        return;
      }

      const link = document.createElement("a");
      link.href = detailFileUrl;
      link.target = "_blank";
      link.rel = "noreferrer";
      link.download = detail.uploaded_files?.[0]?.file_name || buildPayrollFileName(detail.employee_name, detail.year, detail.month);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    const element = pdfContentRef.current;
    if (!element) {
      alert("PDF로 변환할 급여명세서 내용이 없습니다.");
      return;
    }

    setGeneratingPdf(true);
    try {
      const canvas = await html2canvas(element, { scale: 2, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(buildPayrollFileName(detail.employee_name, detail.year, detail.month));
    } catch (err) {
      console.error("급여명세서 PDF 생성 실패", err);
      alert("PDF 생성에 실패했습니다.");
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <section className="admin-page">
      <header className="detail-header">
        <button className="back-btn" type="button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h2 className="section-title">PDF 다운로드</h2>
      </header>

      {loading && <p className="loading-text">정보를 불러오는 중입니다...</p>}
      {error && <p className="error-text">{error}</p>}

      {detail && (
        <div ref={detailIsFileOnly ? undefined : pdfContentRef} style={{ backgroundColor: 'white', padding: '32px', borderRadius: '20px', marginTop: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
            <div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1d1d1f', marginBottom: '8px' }}>
                {detailIsFileOnly ? getPayrollTitle(detail) : `${detail.employee_name} (근무자)`}
              </h3>
              <div style={{ display: 'flex', gap: '24px', color: '#1d1d1f', fontSize: '16px' }}>
                <span><strong>유형</strong> {getPayrollTypeLabel(detail)}</span>
                <span><strong>입사일</strong> {detail.hire_date || "-"}</span>
                <span><strong>퇴사일</strong> {detail.resignation_date || "-"}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {detailIsFileOnly && canPreviewDetailFile && (
                <button
                  style={{
                    backgroundColor: 'white', color: '#1d1d1f', padding: '12px 16px', borderRadius: '8px',
                    fontWeight: '500', fontSize: '16px', cursor: 'pointer', border: '1px solid #e7e8ef'
                  }}
                  onClick={() => window.open(detailFileUrl, "_blank", "noopener,noreferrer")}
                >
                  파일 보기
                </button>
              )}
              <button 
                style={{
                  backgroundColor: '#1d1d1f', color: 'white', padding: '12px 16px', borderRadius: '8px',
                  fontWeight: '500', fontSize: '16px',
                  cursor: detailIsFileOnly && !canPreviewDetailFile ? 'not-allowed' : generatingPdf ? 'wait' : 'pointer',
                  border: 'none',
                  opacity: detailIsFileOnly && !canPreviewDetailFile ? 0.5 : 1
                }}
                onClick={handleDownloadPdf}
                disabled={generatingPdf || (detailIsFileOnly && !canPreviewDetailFile)}
              >
                {generatingPdf ? "PDF 생성 중..." : detailIsFileOnly ? "파일 다운로드" : "PDF 다운로드"}
              </button>
            </div>
          </div>

          {detailIsFileOnly ? (
            <div style={{ border: '1px solid #e7e8ef', borderRadius: '12px', overflow: 'hidden', height: 'calc(100vh - 260px)' }}>
              {canPreviewDetailFile ? (
                <iframe
                  src={`${detailFileUrl}#toolbar=0`}
                  width="100%"
                  height="100%"
                  style={{ border: 'none', display: 'block' }}
                  title="Payroll PDF Viewer"
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666874', padding: '32px', textAlign: 'center' }}>
                  <div>
                    <h3 style={{ margin: '0 0 12px', color: '#1d1d1f', fontSize: '18px' }}>급여명세서 파일을 불러오지 못했습니다.</h3>
                    <p style={{ margin: 0, lineHeight: 1.6 }}>
                      등록된 파일을 찾을 수 없거나 아직 준비되지 않았습니다.<br />
                      잠시 후 다시 시도하거나 관리자에게 문의해 주세요.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
          <div style={{ display: 'flex', gap: '40px' }}>
            {/* 왼쪽: 급여명세 */}
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e7e8ef' }}>
                급여명세
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <DetailRow label="근무자" value={detail.employee_name} />
                <DetailRow label="발급일" value={new Date(detail.created_at).toLocaleDateString()} />
                <DetailRow label="주민번호" value={detail.resident_id_masked} />
                <DetailRow label="총 근무 시간" value={`${detail.total_work_minutes} 분`} />
                <DetailRow label="시급" value={`${detail.hourly_wage.toLocaleString()} 원`} />
                <DetailRow label="기본급" value={`${detail.base_pay.toLocaleString()} 원`} />
                <DetailRow label="주휴수당" value={`${detail.weekly_allowance.toLocaleString()} 원`} />
                <DetailRow label="기타정산금" value={`${(detail.other_settlement_amount ?? 0).toLocaleString()} 원`} />
                <DetailRow label="연장수당" value={`${detail.overtime_pay.toLocaleString()} 원`} />
              </div>
            </div>

            {/* 오른쪽: 공제항목 */}
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e7e8ef' }}>
                공제항목
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <DetailRow label="국민연금" value={`${detail.national_pension.toLocaleString()} 원`} />
                <DetailRow label="건강보험" value={`${detail.health_insurance.toLocaleString()} 원`} />
                <DetailRow label="고용보험" value={`${detail.employment_insurance.toLocaleString()} 원`} />
                <DetailRow label="장기요양보험료" value={`${detail.long_term_care_insurance.toLocaleString()} 원`} />
                <DetailRow label="소득세" value={`${detail.income_tax.toLocaleString()} 원`} />
                <DetailRow label="지방소득세" value={`${detail.local_income_tax.toLocaleString()} 원`} />
                <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e7e8ef' }}>
                  <DetailRow label="공제 총액" value={`${detail.total_deduction.toLocaleString()} 원`} bold />
                  <div style={{ marginTop: '20px' }}>
                    <DetailRow label="실 수령액" value={`${detail.net_pay.toLocaleString()} 원`} bold color="#5696ff" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      )}
    </section>
  );
};

const DetailRow = ({ label, value, bold = false, color = '#1d1d1f' }: { label: string; value: string; bold?: boolean; color?: string }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <span style={{ fontSize: '14px', fontWeight: '500', color: '#1d1d1f' }}>{label}</span>
    <div style={{ backgroundColor: '#fbfbfb', border: '1px solid #e7e8ef', borderRadius: '12px', padding: '16px' }}>
      <span style={{ fontSize: '14px', fontWeight: bold ? '600' : '400', color }}>{value}</span>
    </div>
  </div>
);

export default BranchPayrollDetailPage;
